import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import {
  Button,
  Flex,
  Grid,
  Label,
  FormHeading,
  Box,
  Text,
  Toastify,
  Tooltip,
} from "atoms";
import { FormInput, FormSelect } from "molecules/FormInput";
import Table from "molecules/Table";
import MemoAdd from "assets/icons/Add";
import MemoClose from "assets/icons/Close";
import { debounce, formatCurrency, optionFetch } from "utils/utilities";
import { usePropsoch } from "contexts/Propsoch";
import { useParams } from "react-router-dom";

const validationSchema = Yup.object().shape({
  pricing: Yup.array().of(
    Yup.object().shape({
      typologyId: Yup.string()
        .typeError("Typology is required")
        .required("Typology is required"),
      ticketPrice: Yup.number()
        .min(0, "Value should be greater than 0")
        .typeError("Invalid number")
        .required("Ticket price is required"),
      floor: Yup.number()
        .min(0, "Value should be greater than 0")
        .typeError("Invalid number")
        .required("Floor is required"),
    })
  ),
});

const columns = [
  {
    title: "Typology",
    render: (row) => <>{row.typology}</>,
  },
  {
    title: "Ticket Price ",
    render: (row) => <>{row.priceSalableArea}</>,
  },
  // {
  //   title: "Price on Carpet Area",
  //   field: "priceCarpetArea",
  //   render: (row) => <>{row.priceCarpetArea}</>,
  // },
];

const defPricing = {
  typologyId: "",
  ticketPrice: "",
  averagePriceOnCarpetArea: "",
  averagePriceOnSaleableArea: "",
  beAveragePriceOnCarpetArea: "",
  priceList: "",
  floor: "",
};

export const PricingForm = ({
  onNext,
  onPrevious,
  bindHitOnSave,
  fetchProperty,
  rootProperty,
}) => {
  const {
    actions: {
      getPropertyListingPricing,
      createPropertyListingPricing,
      updatePropertyListingPricing,
      calculatePricing,
    },
  } = usePropsoch();

  const { id } = useParams();

  // typology fetch
  const [typologies, setTypologies] = useState([]);

  useEffect(() => {
    // typology
    id &&
      optionFetch(
        `/properties/pricing/typology/${id}`,
        "typology",
        "typology"
      ).then((data) => {
        setTypologies(data);
      });
  }, [id]);

  // fetch property
  const [property, setProperty] = useState(null);

  const adjustPropertyGet = useCallback(
    async (data) => {
      setProperty(data);

      let pricing = [];

      if (data) {
        const pricingFomatted = await Promise.all(
          data?.pricing?.map(async (i) => {
            const pricingItem = {
              ...i,
              typologyId: `${i?.typlogyName} (${i?.reraSaleableArea})`,
              ticketPrice: i?.trackingTicketPrice, // user tracking price contriol
            };

            const dataCalc = await calculatePricing({
              propertyId: id,
              ticketPrice: pricingItem?.ticketPrice,
              typologyName: pricingItem?.typlogyName,
              reraSaleableArea: pricingItem?.reraSaleableArea,
            });
            pricingItem.averagePriceOnCarpetArea = formatCurrency(
              dataCalc?.averagePriceOnCarpetArea
            );
            pricingItem.beAveragePriceOnCarpetArea =
              dataCalc?.averagePriceOnCarpetArea;

            pricingItem.averagePriceOnSaleableArea = formatCurrency(
              dataCalc?.averagePriceOnSaleableArea
            );
            pricingItem.priceList = dataCalc?.priceList;
            return pricingItem;
          })
        );

        pricing = pricingFomatted;
      }

      // reformat typology
      data &&
        setProperty((prev) => ({
          ...prev,
          pricing,
        }));
    },
    [id, calculatePricing]
  );

  useEffect(() => {
    id &&
      getPropertyListingPricing(id)?.then((data) => {
        adjustPropertyGet(data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const onSubmit = useCallback(
    ({ dirty, pricing }) => {
      setSubmitLoading(true);
      if (dirty) {
        const formatPricing = pricing
          ?.map((item) => {
            // current typology
            const curTypology = typologies?.find(
              (i) => i.value === item?.typologyId
            );

            const otherTypology = typologies?.filter(
              (i) =>
                i?.data?.typologyId === curTypology?.data?.typologyId &&
                i?.data?.reraSaleableArea !==
                  curTypology?.data?.reraSaleableArea
            );
            // asked by BE
            const otsMap = otherTypology?.map((otsItem) => ({
              typologyId: otsItem?.data?.typologyId,
              reraSaleableArea: otsItem?.data?.reraSaleableArea,
              ticketPrice: item?.priceList?.find(
                (i) => i?.typology === otsItem?.value
              )?.ticketPrice,
              trackingTicketPrice: item?.ticketPrice,
              floor: item?.floor,
              averagePriceOnCarpetArea: item?.priceList?.find(
                (i) => i?.typology === otsItem?.value
              )?.averagePriceOnCarpetArea,
            }));

            return [
              {
                // typologyId: item?.typologyId,
                typologyId: curTypology?.data?.typologyId,
                reraSaleableArea: typologies?.find(
                  (i) => i.value === item?.typologyId
                )?.data?.reraSaleableArea,
                ticketPrice: item?.ticketPrice,
                // user ticket price tracking
                trackingTicketPrice: item?.ticketPrice,
                floor: item?.floor,
                averagePriceOnCarpetArea: item?.beAveragePriceOnCarpetArea,
              },
              ...otsMap,
            ];
          })
          ?.reduce((acc, cur) => [...acc, ...cur], []);

        const formattedValues = {
          propertyId: id,
          pricing: formatPricing,
        };
        if (property) {
          updatePropertyListingPricing(formattedValues, id, rootProperty?.name)
            .then(() => {
              Toastify("success", "Pricing updated");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Pricing not updated");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        } else {
          createPropertyListingPricing(formattedValues, rootProperty?.name)
            .then((data) => {
              Toastify("success", "Pricing created");
              fetchProperty()?.then(() => {
                onNext(data?.id);
              });
            })
            .catch((e) => {
              Toastify("error", "Pricing not created");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        }
      } else {
        if (id) {
          onNext();
        }
      }
    },
    [
      createPropertyListingPricing,
      id,
      updatePropertyListingPricing,
      onNext,
      fetchProperty,
      property,
      rootProperty,
      typologies,
    ]
  );

  return (
    <Formik
      initialValues={{ pricing: property?.pricing || [defPricing] }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue, dirty, submitForm }) => {
        bindHitOnSave(() => {
          setFieldValue("dirty", dirty);
          submitForm();
        });
        return (
          <Form>
            <FormHeading text="Pricing" />
            <Grid gridGap="3.2rem" mt="2.4rem">
              {[...values?.pricing].map((item, index) => (
                <Grid
                  border="1px solid"
                  borderColor="neutral.200"
                  p="2.4rem"
                  gridRowGap="2.4rem"
                  borderRadius="1rem"
                  key={index}
                >
                  {index > 0 && (
                    <Flex
                      alignItems="center"
                      color="accents.100"
                      cursor="pointer"
                      position="absolute"
                      right="2rem"
                      top="2.4rem"
                      zIndex={1}
                      onClick={() => {
                        let duplicate = JSON.parse(
                          JSON.stringify(values?.pricing)
                        );
                        duplicate = [
                          ...duplicate?.slice(0, index),
                          ...duplicate?.slice(index + 1),
                        ];
                        setFieldValue("pricing", duplicate);
                      }}
                    >
                      <MemoClose height="1.3rem" width="1.3rem" />
                      <Text
                        color="accents.100"
                        fontFamily="NeometricBold"
                        ml="1rem"
                      >
                        Remove
                      </Text>
                    </Flex>
                  )}
                  <Grid gridTemplateColumns="1fr 1fr" gridGap="2.4rem">
                    <Grid gridRowGap="1.6rem">
                      <Label text="Typology" req />
                      <FormSelect
                        name={`pricing.[${index}].typologyId`}
                        placeholder="Select drop down"
                        options={typologies?.filter(
                          (i) =>
                            !values?.pricing?.find((t) =>
                              t.typologyId?.includes(i.data.typologyName)
                            ) ||
                            values?.pricing?.[index]?.typologyId?.includes(
                              i.data.typologyName
                            )
                          // i.value === values?.pricing?.[index]?.typologyId
                        )}
                        onChange={(e) => {
                          if (values?.pricing?.[index]?.ticketPrice) {
                            setFieldValue(`pricing.[${index}].loading`, true);
                            debounce(() => {
                              const typologyData = typologies?.find(
                                (i) => i.label === e
                              );
                              calculatePricing({
                                propertyId: id,
                                ticketPrice:
                                  values?.pricing?.[index]?.ticketPrice,
                                typologyName: typologyData?.data?.typologyName,
                                reraSaleableArea:
                                  typologyData?.data?.reraSaleableArea,
                              })
                                ?.then((data) => {
                                  // for be purpose
                                  setFieldValue(
                                    `pricing.[${index}].beAveragePriceOnCarpetArea`,
                                    data?.averagePriceOnCarpetArea
                                  );

                                  // for be purpose ends
                                  setFieldValue(
                                    `pricing.[${index}].averagePriceOnCarpetArea`,
                                    formatCurrency(
                                      data?.averagePriceOnCarpetArea
                                    )
                                  );
                                  setFieldValue(
                                    `pricing.[${index}].averagePriceOnSaleableArea`,
                                    formatCurrency(
                                      data?.averagePriceOnSaleableArea
                                    )
                                  );
                                  setFieldValue(
                                    `pricing.[${index}].priceList`,
                                    data?.priceList
                                  );
                                })
                                ?.finally(() => {
                                  setFieldValue(
                                    `pricing.[${index}].loading`,
                                    false
                                  );
                                });
                            }, 1000);
                          }
                        }}
                        req
                      />
                    </Grid>
                    <Grid
                      gridRowGap="1.6rem"
                      css={{
                        "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button":
                          {
                            "-webkit-appearance": "none",
                            margin: 0,
                          },
                        "input[type=number]": {
                          "-moz-appearance": "textfield",
                        },
                      }}
                    >
                      <Label text="Ticket Price" req />
                      <Tooltip
                        render={
                          !!!values?.pricing?.[index]?.typologyId
                            ? "Select typology"
                            : ""
                        }
                        maxWidth="none !important"
                        isTooltipArrow
                        // {...tooltipProps}
                      >
                        <FormInput
                          name={`pricing.[${index}].ticketPrice`}
                          type="number"
                          placeholder="Enter Number"
                          req
                          disabled={!!!values?.pricing?.[index]?.typologyId}
                          onChange={(e) => {
                            setFieldValue(
                              `pricing.[${index}].ticketPrice`,
                              e?.target?.value
                            );
                            setFieldValue(`pricing.[${index}].loading`, true);
                            debounce(() => {
                              const typologyData = typologies?.find(
                                (i) =>
                                  i.label ===
                                  values?.pricing?.[index]?.typologyId
                              );

                              calculatePricing({
                                propertyId: id,
                                ticketPrice: e?.target?.value,
                                typologyName: typologyData?.data?.typologyName,
                                reraSaleableArea:
                                  typologyData?.data?.reraSaleableArea,
                              })
                                ?.then((data) => {
                                  // for be purpose
                                  setFieldValue(
                                    `pricing.[${index}].beAveragePriceOnCarpetArea`,
                                    data?.averagePriceOnCarpetArea
                                  );

                                  // for be purpose ends
                                  setFieldValue(
                                    `pricing.[${index}].averagePriceOnCarpetArea`,
                                    formatCurrency(
                                      data?.averagePriceOnCarpetArea
                                    )
                                  );
                                  setFieldValue(
                                    `pricing.[${index}].averagePriceOnSaleableArea`,
                                    formatCurrency(
                                      data?.averagePriceOnSaleableArea
                                    )
                                  );
                                  setFieldValue(
                                    `pricing.[${index}].priceList`,
                                    data?.priceList
                                  );
                                })
                                ?.finally(() => {
                                  setFieldValue(
                                    `pricing.[${index}].loading`,
                                    false
                                  );
                                });
                            }, 1000);
                          }}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid gridRowGap="1.6rem">
                      <Label text="Average Price on Saleable Area" req />
                      <FormInput
                        name={`pricing.[${index}].averagePriceOnSaleableArea`}
                        placeholder="Enter Number"
                        loading={values?.pricing?.[index]?.loading}
                        req
                        disabled
                      />
                    </Grid>
                    <Grid gridRowGap="1.6rem">
                      <Label text="Average Price on Carpet Area " req />
                      <FormInput
                        name={`pricing.[${index}].averagePriceOnCarpetArea`}
                        placeholder="Enter Number"
                        req
                        loading={values?.pricing?.[index]?.loading}
                        disabled
                      />
                    </Grid>
                    <Grid gridRowGap="1.6rem">
                      <Label text="Floor" req />
                      <FormInput
                        name={`pricing.[${index}].floor`}
                        // type="number"
                        placeholder="Enter Number"
                        req
                      />
                    </Grid>
                  </Grid>
                  <Table
                    tableHeader={columns.map(
                      ({ title, rightAligned, sortKey }) => ({
                        name: title,
                        rightAligned,
                        sortKey,
                        containerProps: {
                          fontSize: "1.4rem",
                          fontWeight: "600",
                        },
                      })
                    )}
                    borderColor="neutral.500"
                  >
                    <Box as="tbody" bg="white">
                      {(values?.pricing?.[index]?.priceList || [])?.map(
                        (item, index) => (
                          <tr key={index}>
                            <td>{item?.typology}</td>
                            <td>{formatCurrency(item?.ticketPrice)}</td>
                          </tr>
                        )
                      )}
                    </Box>
                  </Table>
                </Grid>
              ))}

              <Flex
                alignItems="center"
                color="secondary.700"
                cursor="pointer"
                onClick={() => {
                  const duplicate = JSON.parse(JSON.stringify(values?.pricing));
                  duplicate.push(JSON.parse(JSON.stringify(defPricing)));
                  setFieldValue("pricing", duplicate);
                }}
              >
                <MemoAdd height="1.3rem" width="1.3rem" />
                <Text
                  color="secondary.700"
                  fontFamily="NeometricBold"
                  ml="1rem"
                >
                  Add New
                </Text>
              </Flex>

              <Flex my="2rem" gridGap="1.6rem">
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primaryOutline"
                  height="4.8rem"
                  onClick={onPrevious}
                >
                  Previous
                </Button>
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primary"
                  height="4.8rem"
                  type="submit"
                  loading={submitLoading}
                  onClick={() => {
                    setFieldValue("dirty", dirty);
                    submitForm();
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
